
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ImageUpload } from '@/components/ui/image-upload';

interface Organization {
  id: string;
  name: string;
  description?: string;
  logo_url?: string;
  website?: string;
}

interface OrganizationFormProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  editingOrg: Organization | null;
  formData: {
    name: string;
    description: string;
    logo_url: string;
    website: string;
  };
  setFormData: React.Dispatch<React.SetStateAction<{
    name: string;
    description: string;
    logo_url: string;
    website: string;
  }>>;
  onSubmit: (e: React.FormEvent) => void;
  isLoading: boolean;
}

export const OrganizationForm = ({
  isOpen,
  onOpenChange,
  editingOrg,
  formData,
  setFormData,
  onSubmit,
  isLoading
}: OrganizationFormProps) => {
  const handleImageUploaded = (url: string) => {
    setFormData(prev => ({ ...prev, logo_url: url }));
  };

  const handleImageRemoved = () => {
    setFormData(prev => ({ ...prev, logo_url: '' }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>
              {editingOrg ? 'Edit Organization' : 'Create New Organization'}
            </DialogTitle>
            <DialogDescription>
              {editingOrg ? 'Update organization details' : 'Create a new organization to manage clubs and users'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <ImageUpload
              currentImageUrl={formData.logo_url}
              onImageUploaded={handleImageUploaded}
              onImageRemoved={handleImageRemoved}
              label="Organization Logo"
              bucketName="profile-images"
            />
            
            <div className="grid gap-2">
              <Label htmlFor="name">Organization Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter organization name"
                required
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter organization description"
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                type="url"
                value={formData.website}
                onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://example.com"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : editingOrg ? 'Update' : 'Create'} Organization
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
